import { cn } from '@nextui-org/react';
import 'highlight.js/styles/github-dark.css';
import { memo, useMemo } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

const markdownRemarkPlugins = [remarkGfm];
const markdownRehypePlugins = [rehypeSanitize, rehypeHighlight];

function getMarkdownComponents(isUser: boolean): Components {
  return {
    p: ({ children }) => <p className="text-sm">{children}</p>,
    h1: ({ children }) => <h1 className="text-lg font-bold">{children}</h1>,
    h2: ({ children }) => <h2 className="text-base font-bold">{children}</h2>,
    h3: ({ children }) => <h3 className="text-sm font-bold">{children}</h3>,
    ul: ({ children }) => <ul className="text-sm list-disc">{children}</ul>,
    ol: ({ children }) => <ol className="text-sm list-decimal">{children}</ol>,
    li: ({ children }) => <li className="mb-1">{children}</li>,
    code: ({ className, children, ...props }) => {
      const isBlock = /language-(\w+)/.test(className || '');

      if (isBlock) {
        return (
          <code
            className={cn(className, 'text-sm !bg-transparent !text-inherit')}
            {...props}
          >
            {children}
          </code>
        );
      }

      return (
        <code
          className={cn(
            'rounded text-sm font-mono',
            isUser
              ? 'bg-gray-900/85 text-gray-100'
              : 'bg-gray-200 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
          )}
          {...props}
        >
          {children}
        </code>
      );
    },
    pre: ({ children, ...props }) => (
      <pre
        className="my-3 overflow-x-auto rounded-md text-sm [&_.hljs]:!bg-transparent [&_code]:!bg-transparent [&_code]:!text-inherit"
        {...props}
      >
        {children}
      </pre>
    ),
    blockquote: ({ children }) => (
      <blockquote
        className={cn(
          'my-3 border-l-4 pl-4 text-sm italic',
          isUser
            ? 'border-white/40 text-white/90'
            : 'border-gray-300 text-gray-600 dark:border-gray-500 dark:text-gray-300'
        )}
      >
        {children}
      </blockquote>
    ),
    a: ({ href, children }) => (
      <a
        href={href}
        className={cn(
          'text-sm underline-offset-2 hover:underline',
          isUser ? 'text-blue-200' : 'text-blue-600 dark:text-blue-400'
        )}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    table: ({ children }) => (
      <div className="my-3 overflow-x-auto text-sm">
        <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
          {children}
        </table>
      </div>
    ),
    th: ({ children }) => (
      <th
        className={cn(
          'px-3 py-2 text-left text-sm font-semibold',
          isUser
            ? 'bg-white/15 text-white'
            : 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
        )}
      >
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td
        className={cn(
          'border-t px-3 py-2 text-sm',
          isUser
            ? 'border-white/20 text-white'
            : 'border-gray-200 text-gray-800 dark:border-gray-600 dark:text-gray-100'
        )}
      >
        {children}
      </td>
    ),
  };
}

export const MarkdownMessageContent = memo(function MarkdownMessageContent({
  text,
  isUser,
}: {
  text: string;
  isUser: boolean;
}) {
  const markdownComponents = useMemo(
    () => getMarkdownComponents(isUser),
    [isUser]
  );

  return (
    <div
      className={cn(
        'chat-markdown leading-relaxed prose max-w-none',
        'prose-code:before:content-none prose-code:after:content-none',
        'prose-pre:p-0 prose-pre:bg-transparent prose-pre:text-inherit',
        isUser
          ? 'text-white prose-headings:text-white prose-strong:text-white prose-p:text-white prose-li:text-white'
          : 'text-gray-800 dark:prose-invert dark:text-gray-100'
      )}
    >
      <ReactMarkdown
        remarkPlugins={markdownRemarkPlugins}
        rehypePlugins={markdownRehypePlugins}
        components={markdownComponents}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
});
